'use client';

import * as React from 'react';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  FilterIcon,
  PlusIcon,
  SearchIcon,
  ShoppingBagIcon
} from 'lucide-react';

import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import { Card, CardContent } from '@workspace/ui/components/card';
import { DataTableFilter } from '@workspace/ui/components/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@workspace/ui/components/dropdown-menu';
import { InputSearch } from '@workspace/ui/components/input-search';
import { ScrollArea } from '@workspace/ui/components/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@workspace/ui/components/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@workspace/ui/components/sheet';
import { toast } from '@workspace/ui/components/sonner';
import { CenteredSpinner } from '@workspace/ui/components/spinner';
import {
  UnderlinedTabs,
  UnderlinedTabsList,
  UnderlinedTabsTrigger
} from '@workspace/ui/components/tabs';
import { cn } from '@workspace/ui/lib/utils';

import type { GeneratorStepProps } from '~/components/generator/shared/generator-step-props';

export interface ItemSortOption {
  value: string;
  label: string;
}

export interface ItemSelectionStepProps<T extends { id: string }, F = unknown>
  extends GeneratorStepProps,
    React.HTMLAttributes<HTMLDivElement> {
  fetcher: (args: {
    pageIndex: number;
    pageSize: number;
    searchQuery: string;
    filters: Record<string, string[]>;
    sortBy: string;
    sortDirection: 'asc' | 'desc';
  }) => Promise<{ items: T[]; filteredCount: number; totalCount: number } & F>;
  onSelectionChange: (selectedIds: string[]) => void;
  selectedIds: string[];
  ItemCardComponent: React.ComponentType<{
    item: T;
    onSelect: (item: T) => void;
    isSelected: boolean;
    disabled: boolean;
    selectedIds?: string[];
    onSelectionChange?: (selectedIds: string[]) => void;
  }>;
  SelectedItemCardComponent: React.ComponentType<{
    item: T;
    onRemove: (id: string) => void;
  }>;
  getFilterDefs: (fetcherResult: F | undefined) => Array<{
    name: string;
    title: string;
    options: Array<{ value: string; label: string }>;
  }>;
  sortOptions: ItemSortOption[];
  /**
   * When provided, clicking Add Item will call this.
   * You can optionally accept a callback and invoke it with the newly created item(s)
   * to have them appended locally without a refetch.
   */
  onAddItem?:
    | (() => void | Promise<void>)
    | ((onCreated: (itemOrItems: T | T[]) => void) => void | Promise<void>);
  title: string;
  description: string;
  searchPlaceholder?: string;
  maxSelection?: number;
  pageSize?: number;
  maxSelectionMessage?: string;
  isItemSelected?: (item: T, selectedIds: string[]) => boolean;
  handleItemSelect?: (item: T, selectedIds: string[]) => string[];
  selectedItemsIcon?: React.ComponentType<{ className?: string }>;
  selectedItemsLabel?: string;
  /**
   * Optional generic group filter configuration rendered inline with search.
   * Example uses: switching product source, toggling gender, etc.
   */
  groupFilter?: {
    options: Array<{ value: string; label: string; icon?: React.ReactNode }>;
    value: string;
    onValueChange: (value: string) => void;
    srLabel?: string;
  };
}

export function ItemSelectionStep<T extends { id: string }, F = unknown>({
  fetcher,
  onSelectionChange,
  selectedIds,
  ItemCardComponent,
  SelectedItemCardComponent,
  getFilterDefs,
  sortOptions,
  onAddItem,
  title,
  description,
  searchPlaceholder,
  maxSelection = 4,
  pageSize = 12,
  maxSelectionMessage = `Maximum ${maxSelection} items allowed`,
  isItemSelected,
  handleItemSelect: handleItemSelectProp,
  selectedItemsIcon: SelectedItemsIcon = ShoppingBagIcon,
  selectedItemsLabel = 'Selected Items',
  className,
  groupFilter,
  ...other
}: ItemSelectionStepProps<T, F>): React.JSX.Element {
  const [items, setItems] = React.useState<T[]>([]);
  const [fetcherResult, setFetcherResult] = React.useState<F>();
  const [totalCount, setTotalCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);

  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
  const [filters, setFilters] = React.useState<Record<string, string[]>>({});
  const [sortBy, setSortBy] = React.useState(sortOptions[0]?.value);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>(
    'asc'
  );
  const [currentPage, setCurrentPage] = React.useState(0);
  const [showMobileSearch, setShowMobileSearch] = React.useState(false);

  // Debounce search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleItemSelect = (item: T) => {
    if (handleItemSelectProp) {
      const newSelectedIds = handleItemSelectProp(item, selectedIds);
      onSelectionChange(newSelectedIds);
    } else {
      const isSelected = selectedIds.includes(item.id);

      if (isSelected) {
        onSelectionChange(selectedIds.filter((id) => id !== item.id));
      } else {
        if (selectedIds.length >= maxSelection) {
          toast.error(maxSelectionMessage);
          return;
        }

        onSelectionChange([...selectedIds, item.id]);
      }
    }
  };

  const fetchItems = React.useCallback(
    async (page: number, append: boolean) => {
      if (page === 0) setIsLoading(true);
      else setIsLoadingMore(true);

      try {
        const result = await fetcher({
          pageIndex: page,
          pageSize,
          searchQuery: debouncedSearchQuery,
          filters,
          sortBy,
          sortDirection
        });

        const { items: newItems, filteredCount, totalCount, ...rest } = result;

        if (append) {
          setItems((prev) => {
            // Filter out duplicates based on item ID
            const existingIds = new Set(prev.map((item) => item.id));
            const uniqueNewItems = newItems.filter(
              (item) => !existingIds.has(item.id)
            );
            return [...prev, ...uniqueNewItems];
          });
        } else {
          setItems(newItems);
        }
        setFetcherResult(rest as F);
        setTotalCount(filteredCount);
        setHasMore((page + 1) * pageSize < filteredCount);
      } catch (error) {
        console.error('Failed to fetch items:', error);
        toast.error('Failed to load items. Please try again.');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [pageSize, debouncedSearchQuery, filters, fetcher, sortBy, sortDirection]
  );

  React.useEffect(() => {
    setCurrentPage(0);
    void fetchItems(0, false);
  }, [debouncedSearchQuery, filters, fetcher, sortBy, sortDirection]);

  const loadMore = React.useCallback(() => {
    if (!isLoadingMore && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      void fetchItems(nextPage, true);
    }
  }, [isLoadingMore, hasMore, currentPage, fetchItems]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (name: string, value: string[]) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const appendCreatedItemsLocally = React.useCallback(
    (created: T | T[]) => {
      const createdItems = Array.isArray(created) ? created : [created];
      setItems((prev) => {
        const existingIds = new Set(prev.map((it) => it.id));
        const uniqueNew = createdItems.filter((it) => !existingIds.has(it.id));
        if (uniqueNew.length === 0) return prev;
        return [...uniqueNew, ...prev];
      });
      setTotalCount((prev) => {
        const prevIds = new Set(items.map((it) => it.id));
        const uniqueNewCount = (
          Array.isArray(created) ? created : [created]
        ).filter((it) => !prevIds.has(it.id)).length;
        return prev + uniqueNewCount;
      });
      setHasMore(
        (currentPage + 1) * pageSize <
          totalCount + (Array.isArray(created) ? created.length : 1)
      );
    },
    [currentPage, pageSize, totalCount, items]
  );

  const handleAddItemClick = React.useCallback(async () => {
    if (!onAddItem) return;
    try {
      const maybeWithCallback = onAddItem as (
        cb: (itemOrItems: T | T[]) => void
      ) => void | Promise<void>;
      const result = maybeWithCallback(appendCreatedItemsLocally);
      if (result instanceof Promise) await result;
    } catch (error) {
      console.error('Failed to add item:', error);
      toast.error('Failed to add item. Please try again.');
    }
  }, [onAddItem, appendCreatedItemsLocally]);

  const filterDefs = getFilterDefs(fetcherResult);
  const activeFiltersCount = Object.values(filters).reduce(
    (acc, val) => acc + val.length,
    0
  );

  return (
    <div
      className={cn(
        'flex h-[calc(100vh-15rem)] w-full flex-col gap-4',
        className
      )}
      {...other}
    >
      <div className="space-y-2">
        <h1 className="text-xl font-semibold leading-none tracking-tight lg:text-2xl">
          {title}
        </h1>
        <p className="text-xs text-muted-foreground lg:text-sm">
          {description}
        </p>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Mobile Controls */}
        <div className="relative flex w-full items-center gap-2 sm:hidden">
          {groupFilter && (
            <Select
              value={groupFilter.value}
              onValueChange={groupFilter.onValueChange}
            >
              <SelectTrigger
                aria-label={groupFilter.srLabel ?? 'Group filter'}
                className=""
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {groupFilter.options.map((opt) => (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                  >
                    <div className="flex flex-row items-center gap-2 pr-2">
                      {opt.icon}
                      {opt.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <div className="relative">
                  <Button
                    variant="outline"
                    size="icon"
                  >
                    <FilterIcon className="h-4 w-4" />
                  </Button>
                  {activeFiltersCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full p-0 text-xs"
                    >
                      {activeFiltersCount}
                    </Badge>
                  )}
                </div>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Filter and Sort</SheetTitle>
                </SheetHeader>
                <div className="space-y-6 p-4">
                  <div>
                    <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                      Sort by
                    </h3>
                    <SortControl
                      options={sortOptions}
                      value={sortBy}
                      onValueChange={setSortBy}
                      sortDirection={sortDirection}
                      onSortDirectionChange={setSortDirection}
                    />
                  </div>
                  {filterDefs.map((filter) => (
                    <div key={filter.name}>
                      <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                        {filter.title}
                      </h3>
                      <DataTableFilter
                        title={filter.title}
                        options={filter.options}
                        selected={filters[filter.name] || []}
                        onChange={(value) =>
                          handleFilterChange(filter.name, value)
                        }
                      />
                    </div>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowMobileSearch(true)}
            >
              <SearchIcon className="h-4 w-4 shrink-0" />
            </Button>
          </div>
          {showMobileSearch && (
            <div className="absolute inset-0 z-50 bg-background">
              <InputSearch
                autoFocus
                alwaysShowClearButton
                placeholder={searchPlaceholder ?? 'Search...'}
                className="h-12 w-full border-none !ring-0 bg-background"
                containerClassName="h-12 w-full"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onClear={() => setShowMobileSearch(false)}
              />
            </div>
          )}
        </div>

        {/* Desktop Controls */}
        <div className="hidden flex-1 items-center gap-2 sm:flex">
          {groupFilter && (
            <UnderlinedTabs
              value={groupFilter.value}
              onValueChange={groupFilter.onValueChange}
              className="-ml-2"
            >
              <UnderlinedTabsList className="mr-2 h-12 max-h-12 min-h-12 gap-x-2 border-none">
                {groupFilter.options.map((opt) => (
                  <UnderlinedTabsTrigger
                    key={opt.value}
                    value={opt.value}
                    className="mx-0 border-t-4 border-t-transparent"
                  >
                    <div className="flex flex-row items-center gap-2 rounded-md px-2 py-1 hover:bg-accent">
                      {opt.icon}
                      {opt.label}
                    </div>
                  </UnderlinedTabsTrigger>
                ))}
              </UnderlinedTabsList>
            </UnderlinedTabs>
          )}
          {filterDefs.map((filter) => (
            <DataTableFilter
              key={filter.name}
              title={filter.title}
              options={filter.options}
              selected={filters[filter.name] || []}
              onChange={(value) => handleFilterChange(filter.name, value)}
            />
          ))}
          <div className="ml-auto flex items-center gap-2">
            <SortControl
              options={sortOptions}
              value={sortBy}
              onValueChange={setSortBy}
              sortDirection={sortDirection}
              onSortDirectionChange={setSortDirection}
            />
            <InputSearch
              placeholder={searchPlaceholder ?? 'Search...'}
              className="w-[240px]"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            />
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <ScrollArea className="h-full min-h-full w-full">
          <div className="space-y-4">
            {isLoading ? (
              <CenteredSpinner />
            ) : items.length > 0 || onAddItem ? (
              <>
                <div className="hidden items-center justify-between sm:flex">
                  <p className="text-sm text-muted-foreground">
                    {totalCount} {totalCount === 1 ? 'item' : 'items'} found
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                  {onAddItem && (
                    <Card
                      className="group h-48 w-full cursor-pointer border-2 border-dashed border-muted-foreground/30 p-0 transition-all hover:border-primary/50 hover:shadow-md md:h-64"
                      onClick={handleAddItemClick}
                    >
                      <CardContent className="h-full p-0">
                        <div className="flex h-full w-full items-center justify-center bg-muted/20">
                          <div className="flex flex-col items-center justify-center gap-2 text-center">
                            <PlusIcon className="h-8 w-8 text-muted-foreground" />
                            <span className="text-sm font-medium text-muted-foreground">
                              Add Item
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {items.map((item) => {
                    const isCurrentlySelected = isItemSelected
                      ? isItemSelected(item, selectedIds)
                      : selectedIds.includes(item.id);
                    return (
                      <ItemCardComponent
                        key={item.id}
                        item={item}
                        onSelect={handleItemSelect}
                        isSelected={isCurrentlySelected}
                        disabled={
                          selectedIds.length >= maxSelection &&
                          !isCurrentlySelected
                        }
                        selectedIds={selectedIds}
                        onSelectionChange={onSelectionChange}
                      />
                    );
                  })}
                </div>
                {isLoadingMore && (
                  <div className="flex justify-center py-4">
                    <CenteredSpinner />
                  </div>
                )}

                <div className="flex justify-center py-4">
                  {' '}
                  {/* We need the padding regardless */}
                  {hasMore && !isLoadingMore && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={loadMore}
                    >
                      Load More
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="flex h-full min-h-[200px] w-full flex-col items-center justify-center rounded-lg border border-dashed">
                <div className="flex flex-col items-center justify-center gap-4">
                  <p className="text-sm text-muted-foreground">
                    {searchQuery ||
                    Object.values(filters).some((f) => f.length > 0)
                      ? 'No items match your filters.'
                      : 'No items found.'}
                  </p>
                  {onAddItem && (
                    <Button
                      type="button"
                      variant="outline"
                      size="default"
                      onClick={handleAddItemClick}
                    >
                      <PlusIcon className="mr-2 h-4 w-4" />
                      Add Item
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

function SortControl({
  options,
  value,
  onValueChange,
  sortDirection,
  onSortDirectionChange
}: {
  options: ItemSortOption[];
  value: string;
  onValueChange: (value: string) => void;
  sortDirection: 'asc' | 'desc';
  onSortDirectionChange: (direction: 'asc' | 'desc') => void;
}) {
  const selectedOptionLabel =
    options.find((option) => option.value === value)?.label ?? '...';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between sm:w-[200px]"
        >
          <span>
            {selectedOptionLabel} ({sortDirection === 'asc' ? 'Asc' : 'Desc'})
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={onValueChange}
        >
          {options.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            onSortDirectionChange(sortDirection === 'asc' ? 'desc' : 'asc');
          }}
        >
          {sortDirection === 'asc' ? (
            <>
              <ArrowDown className="mr-2 h-4 w-4" />
              <span>Change to Descending</span>
            </>
          ) : (
            <>
              <ArrowUp className="mr-2 h-4 w-4" />
              <span>Change to Ascending</span>
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
